import 'react-image-crop/dist/ReactCrop.css'
import './ImageUploader.scss'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import { ReactCrop } from 'react-image-crop'
import { formEvent } from '../Form/Form'
import { getCroppedImg } from '../../helpers/image'
import t from '../../helpers/i18n'
import { useDropzone } from 'react-dropzone'

const ImageUploader = ({
  name,
  label,
  src,
  aspect,
  finalWidth,
  finalHeight,
  circular,
  required,
  ref
}) => {
  const [imageData, setImageData] = useState()
  const [fileData, setFileData] = useState()
  const [crop, setCrop] = useState()
  const image = useRef()
  const element = useRef()

  const propagateValue = useCallback(() => {
    if (ref) ref.current = fileData
    if (name && fileData) {
      element.current.dispatchEvent(
        formEvent({
          action: 'PENDING',
          name,
          value: {
            ...fileData,
            type: 'image/png',
            get: async () => {
              if (crop && crop.width > 0 && crop.height > 0) {
                return getCroppedImg(
                  image.current,
                  crop,
                  finalWidth,
                  finalHeight
                )
              } else {
                return Promise.reject({ error: 'INVALID_CROP' })
              }
            }
          }
        })
      )
    }
  }, [name, ref, fileData, crop, finalWidth, finalHeight])

  const handleCrop = useCallback((crop) => {
    if (image.current) {
      if (crop.width > image.current.width) crop.width = image.current.width
      if (crop.height > image.current.height) crop.height = image.current.height
      if (crop.x < 0) crop.x = 0
      if (crop.y < 0) crop.y = 0
      if (crop.x + crop.width > image.current.width) crop.x = image.current.width - crop.width
      if (crop.y + crop.height > image.current.height) crop.y = image.current.height - crop.height

      const currentAspect = crop.width / crop.height
      if (aspect && aspect !== currentAspect) {
        crop.width = Math.min(crop.width, aspect * crop.height)
        crop.height = crop.width / aspect
      }
    }
    setCrop(crop)
  }, [aspect])

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0]
    setImageData(URL.createObjectURL(file))
    const { name, type, size } = file
    setFileData({ name, type, size })
  }, [])

  const onImageLoaded = useCallback((event) => {
    image.current = event.target
    const newCrop = { unit: 'px', x: 0, y: 0, width: image.current.width, height: image.current.height }
    const currentAspect = newCrop.width / newCrop.height
    if (aspect && aspect !== currentAspect) {
      newCrop.width = Math.min(newCrop.width, aspect * newCrop.height)
      newCrop.height = newCrop.width / aspect
    }
    setCrop(newCrop)
    return true
  }, [aspect])

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: 'image/*'
  })

  useEffect(() => {
    const oldImageData = imageData
    return () => {
      if (oldImageData) {
        URL.revokeObjectURL(oldImageData)
      }
    }
  }, [imageData])

  useEffect(propagateValue, [propagateValue])

  useEffect(() => {
    if (name) {
      element.current.dispatchEvent(
        formEvent({
          action: 'REQUIRED',
          name,
          required
        })
      )
    }
  }, [name, required])

  useEffect(() => {
    const _element = element.current
    return () => {
      if (name) {
        _element.dispatchEvent(
          formEvent({
            action: 'REMOVE',
            name
          })
        )
      }
    }
  }, [name])

  return (
    <div className="image-uploader" ref={element}>
      <div className="-label text-label">{label}</div>
      <div {...getRootProps()} className="-dropzone">
        <input {...getInputProps()} />
        {!imageData && !src && (
          <div className='-hint'>
            {t('Drag_the_image_to_this_area_or_click_on')}{' '}
            <button>{t('Upload_image')}</button>
          </div>
        )}
        {!imageData && src && (
          <div className='-preview'>
            <img src={src} alt='' />
            <div className='-hint'>
              {t('Drag_the_image_to_this_area_or_press_the_button')}
              <button>{t('Upload_image')}</button>
            </div>
          </div>
        )}
        {imageData && (
          <div className='-hint'>
            {t('Select_the_desired_area_of_the_image_or_click_on')}{' '}
            <button>{t('Upload_image')}</button>
          </div>
        )}
      </div>
      {imageData && (
        <ReactCrop
          crop={crop}
          onChange={handleCrop}
          ruleOfThirds
          keepSelection
          circularCrop={circular}
        >
          <img
            src={imageData}
            onLoad={onImageLoaded}
            alt=''
          />
        </ReactCrop>
      )}
    </div>
  )
}

export default ImageUploader
